// Ultimate Card Game (UCG) - WORLD_STATE unit packet compaction
//
// A server snapshot carries ~95 fields per unit at 30 Hz, and for any given
// unit nearly all of the transient ones (hackerLockTimer, isBurrowed,
// pinkBeretState, ... ) sit at their idle default. Measured: ~1.8 KB per unit,
// ~2.2 MB/s per client in a 40-unit fight. The server now omits every field
// that equals its idle default (compactSnapshotUnit) and the client restores
// them before the existing applier runs (expandSnapshotUnits), so the applier
// - which treats a missing key as "keep the previous value" - sees exactly the
// full packet it always saw. Lossless by construction.
//
// RULE for new transient unit fields (docs/INVARIANTS.md §16): add the field to
// js/net/stateStream.js AND server/gameSimulation.js as before; if its idle
// value is false / 0 / null / [], also add it here so it is not sent while idle.
// A field that is NOT listed here is simply always sent - correct, just bigger.
// Both ends import this one table, so they can never disagree on a default.

const EMPTY_STACKS = Object.freeze([]);

export const UNIT_FIELD_DEFAULTS = Object.freeze({
  isAir: false,
  isCloaked: false,
  decloakTimer: 0,
  isParachuting: false,
  parachuteTimer: 0,
  parachuteAltitude: 0,
  burstTimer: 0,
  isReloading: false,
  rocketsFired: 0,
  deployProgress: 0,
  underFireTimer: 0,
  smokeCloakTimer: 0,
  smokeGrenadeTriggered: false,
  executionTimer: 0,
  executionTargetId: null,
  executionCooldown: 0,
  targetId: null,
  attackCooldown: 0,
  isAttacking: false,
  speed: 0,
  isPincerStalking: false,
  isHeld: false,
  isEngaged: false,
  edition: null,
  customAuraHex: null,
  tetherTargetId: null,
  beamTargetId: null,
  dualTetherTarget1Id: null,
  dualTetherTarget2Id: null,
  tether1LockTimer: 0,
  isRooted: false,
  isStoppedAtBase: false,
  isGoldenStatue: false,
  hasGoldPedestal: false,
  isFallingStatue: false,
  isCocoon: false,
  cocoonTimer: 0,
  isFallingCocoon: false,
  hasPetrifiedUnit: false,
  hasParasite: false,
  isInsane: false,
  isParasitized: false,
  acidStacks: 0,
  sporeSlowTimer: 0,
  sporeSlowPenalty: 0,
  disarmTimer: 0,
  overstimulationTimer: 0,
  isOverstimulated: false,
  isMineRising: false,
  mineRiseTimer: 0,
  isBurrowing: false,
  burrowTimer: 0,
  isBurrowed: false,
  mineState: null,
  wasBurrowedBeforeRising: false,
  burrowAnchorX: null,
  burrowAnchorY: null,
  incinerateStacks: EMPTY_STACKS,
  plasmaChargeTimer: 0,
  plasmaMorphed: false,
  plasmaTargetIsAir: false,
  isGrowing: false,
  growthTimer: 0,
  snapStrikeTimer: 0,
  snapTargetId: null,
  firstHitDone: false,
  hasDealtFirstHit: false,
  pinkBeretState: null,
  pinkBeretAimTimer: 0,
  pinkBeretTransitionTimer: 0,
  pinkBeretTargetId: null,
  hasFiredInitialRocket: false,
  hackerState: null,
  hackerTargetId: null,
  hackerLockTimer: 0,
  isHacking: false,
  isTranscendent: false,
  transcendentTimer: 0,
  handRevealTimer: 0,
  isDisabled: false,
  disabledByHackerId: null,
  isIndestructible: false,
  _hpRegenFlash: 0,
  isSentinelIdle: false,
  turretAimState: null,
  hasBomb: false,
  pigeonState: null,
  bombTargetId: null,
  bombHoverTimer: 0,
  isFlyingBackward: false,
  isHovering: false,
  pigeonPitch: 0,
  nozzleTilt: 0
});

const DEFAULT_KEYS = Object.keys(UNIT_FIELD_DEFAULTS);

function isDefault(key, value) {
  const d = UNIT_FIELD_DEFAULTS[key];
  if (value === d) return true;
  // Empty stack arrays (server uses a shared frozen [] for "none").
  return d === EMPTY_STACKS && Array.isArray(value) && value.length === 0;
}

// Server: returns a NEW object holding only the fields that differ from their
// idle default (plus every field not in the defaults table: id, name, team, x,
// y, hp, maxHp, facingDir, age, ...). `full` is left untouched.
export function compactSnapshotUnit(full) {
  const out = {};
  for (const key in full) {
    const val = full[key];
    const def = UNIT_FIELD_DEFAULTS[key];
    if (def !== undefined) {
      if (val === def) continue;
      if (def === EMPTY_STACKS && Array.isArray(val) && val.length === 0) continue;
    }
    out[key] = val;
  }
  return out;
}

// Client: restores the omitted idle defaults in place, so downstream code sees
// the complete packet. Idempotent (a full, uncompacted packet passes through
// unchanged), which keeps older servers / tests / recorded fixtures working.
export function expandSnapshotUnits(units) {
  if (!Array.isArray(units)) return units;
  for (let i = 0; i < units.length; i++) {
    const u = units[i];
    if (!u) continue;
    for (let k = 0; k < DEFAULT_KEYS.length; k++) {
      const key = DEFAULT_KEYS[k];
      if (u[key] === undefined) u[key] = UNIT_FIELD_DEFAULTS[key];
    }
  }
  return units;
}

// Server: fully expanded COPY of a compacted unit packet, for recipients that
// did not announce protocol 2 (the shared compacted object must stay untouched,
// it is also sent to the other player).
export function expandedSnapshotUnitCopy(compact) {
  const out = {};
  for (const key in compact) out[key] = compact[key];
  for (let k = 0; k < DEFAULT_KEYS.length; k++) {
    const key = DEFAULT_KEYS[k];
    if (out[key] === undefined) out[key] = UNIT_FIELD_DEFAULTS[key];
  }
  return out;
}
